import { jsx, jsxs } from "react/jsx-runtime";
import { cva } from "@/styled-system/css";
import { List as List$1, Icon } from "@zillow/constellation-10";
import { IconCheckmarkOutline } from "@zillow/constellation-icons";
import React, { createContext, useContext } from "react";
import { resolveSpacing } from "../../utils/token-migration/migrate-responsive.esm.js";
import { migrateSpacingToken, migrateColorToken } from "../../utils/token-migration/v8-to-v10.esm.js";
const listRecipe = cva({
  variants: {
    marginBlockStart: {
      none: {
        marginBlockStart: "none"
      },
      tightest: {
        marginBlockStart: "tightest"
      },
      tighter: {
        marginBlockStart: "tighter"
      },
      tight: {
        marginBlockStart: "tight"
      },
      default: {
        marginBlockStart: "default"
      },
      loose: {
        marginBlockStart: "loose"
      },
      looser: {
        marginBlockStart: "looser"
      },
      "layout.tightest": {
        marginBlockStart: "layout.tightest"
      },
      "layout.tighter": {
        marginBlockStart: "layout.tighter"
      },
      "layout.tight": {
        marginBlockStart: "layout.tight"
      },
      "layout.default": {
        marginBlockStart: "layout.default"
      },
      "layout.loose": {
        marginBlockStart: "layout.loose"
      },
      "layout.looser": {
        marginBlockStart: "layout.looser"
      },
      "layout.loosest": {
        marginBlockStart: "layout.loosest"
      },
      "layout.vast.default": {
        marginBlockStart: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginBlockStart: "layout.vast.loose"
      },
      "layout.vast.looser": {
        marginBlockStart: "layout.vast.looser"
      },
      // Negative margins — the editor's SpacingControls offers -xs…-xxxl for
      // margin attributes, and migrateSpacingToken maps them to negative v10
      // tokens (NEGATIVE_SPACING_MAP). Mirror them here so the string path
      // emits CSS instead of silently dropping the value.
      "-tightest": {
        marginBlockStart: "-tightest"
      },
      "-tighter": {
        marginBlockStart: "-tighter"
      },
      "-default": {
        marginBlockStart: "-default"
      },
      "-loose": {
        marginBlockStart: "-loose"
      },
      "-looser": {
        marginBlockStart: "-looser"
      },
      "-layout.looser": {
        marginBlockStart: "-layout.looser"
      },
      "-layout.vast.default": {
        marginBlockStart: "-layout.vast.default"
      },
      "-layout.vast.loose": {
        marginBlockStart: "-layout.vast.loose"
      }
    },
    marginBlockEnd: {
      none: {
        marginBlockEnd: "none"
      },
      tightest: {
        marginBlockEnd: "tightest"
      },
      tighter: {
        marginBlockEnd: "tighter"
      },
      tight: {
        marginBlockEnd: "tight"
      },
      default: {
        marginBlockEnd: "default"
      },
      loose: {
        marginBlockEnd: "loose"
      },
      looser: {
        marginBlockEnd: "looser"
      },
      "layout.tightest": {
        marginBlockEnd: "layout.tightest"
      },
      "layout.tighter": {
        marginBlockEnd: "layout.tighter"
      },
      "layout.tight": {
        marginBlockEnd: "layout.tight"
      },
      "layout.default": {
        marginBlockEnd: "layout.default"
      },
      "layout.loose": {
        marginBlockEnd: "layout.loose"
      },
      "layout.looser": {
        marginBlockEnd: "layout.looser"
      },
      "layout.loosest": {
        marginBlockEnd: "layout.loosest"
      },
      "layout.vast.default": {
        marginBlockEnd: "layout.vast.default"
      },
      "layout.vast.loose": {
        marginBlockEnd: "layout.vast.loose"
      },
      "layout.vast.looser": {
        marginBlockEnd: "layout.vast.looser"
      },
      // Negative margins — see marginBlockStart above.
      "-tightest": {
        marginBlockEnd: "-tightest"
      },
      "-tighter": {
        marginBlockEnd: "-tighter"
      },
      "-default": {
        marginBlockEnd: "-default"
      },
      "-loose": {
        marginBlockEnd: "-loose"
      },
      "-looser": {
        marginBlockEnd: "-looser"
      },
      "-layout.looser": {
        marginBlockEnd: "-layout.looser"
      },
      "-layout.vast.default": {
        marginBlockEnd: "-layout.vast.default"
      },
      "-layout.vast.loose": {
        marginBlockEnd: "-layout.vast.loose"
      }
    },
    // Adornment color for the checkmark icon (v8 adornmentColor). Values are
    // the v10 tokens migrateColorToken() produces for the editor's picker
    // (scoped to text + brand colors). Literal so Panda extracts them —
    // a runtime color on the css prop would be dropped at build time.
    adornmentColor: {
      "text.neutral": {
        color: "text.neutral"
      },
      "text.subtle": {
        color: "text.subtle"
      },
      "text.accent.brand.hero": {
        color: "text.accent.brand.hero"
      },
      white: {
        color: "white"
      },
      "gray.400": {
        color: "gray.400"
      },
      "aqua.400": {
        color: "aqua.400"
      },
      "green.300": {
        color: "green.300"
      },
      "orange.300": {
        color: "orange.300"
      },
      "orange.600": {
        color: "orange.600"
      },
      "purple.300": {
        color: "purple.300"
      },
      "purple.500": {
        color: "purple.500"
      },
      "blue.600": {
        color: "blue.600"
      },
      "blue.900": {
        color: "blue.900"
      },
      "teal.600": {
        color: "teal.600"
      }
    }
  }
});
const ListItemContext = createContext({});
const List = ({
  appearance,
  verticalSpace,
  adornmentColor,
  marginTop,
  marginBottom,
  paddingBottom,
  children,
  css: cssProp,
  ...rest
}) => {
  const resolvedMarginTop = resolveSpacing(marginTop);
  const resolvedMarginBottom = resolveSpacing(marginBottom);
  const recipeProps = {};
  const responsiveCss = {};
  if (typeof resolvedMarginTop === "string") {
    recipeProps.marginBlockStart = resolvedMarginTop;
  } else if (resolvedMarginTop) {
    responsiveCss.marginBlockStart = resolvedMarginTop;
  }
  if (typeof resolvedMarginBottom === "string") {
    recipeProps.marginBlockEnd = resolvedMarginBottom;
  } else if (resolvedMarginBottom) {
    responsiveCss.marginBlockEnd = resolvedMarginBottom;
  }
  const resolvedVerticalSpace = verticalSpace ? migrateSpacingToken(verticalSpace) : void 0;
  const resolvedPaddingBottom = paddingBottom ? migrateSpacingToken(paddingBottom) : void 0;
  const resolvedAdornmentColor = adornmentColor ? migrateColorToken(adornmentColor) : void 0;
  const isCheckmark = appearance === "checkmark";
  const v10Appearance = isCheckmark || appearance === "horizontalTable" ? "none" : appearance;
  const cssProps = {
    // v10 List.Root sets color: 'List.label' as a base style, overriding parent
    // color inheritance. Force inherit so lists inside colored Groups (e.g. white
    // text on dark backgrounds) render correctly — mirrors the v8 color: 'inherit'
    // applied via &&&&& specificity on StyledList.
    color: "inherit",
    ...listRecipe.raw(recipeProps),
    ...responsiveCss,
    ...resolvedPaddingBottom ? {
      paddingBlockEnd: resolvedPaddingBottom
    } : {}
  };
  const mergedCss = cssProp || Object.keys(cssProps).length > 0 ? Object.assign({}, cssProps, cssProp) : void 0;
  return /* @__PURE__ */ jsx(ListItemContext.Provider, { value: {
    appearance,
    adornmentColor: resolvedAdornmentColor,
    verticalSpace: resolvedVerticalSpace
  }, children: /* @__PURE__ */ jsx(List$1.Root, { appearance: v10Appearance, css: mergedCss, ...rest, children }) });
};
const ListItem = React.forwardRef(({
  css: cssProp,
  children,
  ...rest
}, ref) => {
  const {
    appearance,
    adornmentColor,
    verticalSpace
  } = useContext(ListItemContext);
  const isCheckmark = appearance === "checkmark";
  const itemCss = {
    // Suppress the 8px paddingBlock listItemRecipe applies by default;
    // v8 forced padding-top: 0 at high specificity.
    paddingBlock: "none",
    // Per-item spacing (v8 paddingBottom={verticalSpace}); last is flush.
    ...verticalSpace && verticalSpace !== "none" ? listRecipe.raw({
      marginBlockEnd: verticalSpace
    }) : {},
    "&:last-child": {
      marginBlockEnd: "none"
    },
    // checkmark: [icon][content] flex row with a hanging indent so
    // wrapped lines align under the text, not the icon.
    ...isCheckmark ? {
      display: "flex",
      alignItems: "flex-start",
      gap: "tighter"
    } : {}
  };
  const mergedCss = cssProp ? Object.assign({}, itemCss, cssProp) : itemCss;
  return /* @__PURE__ */ jsxs(List$1.Item, { ref, css: mergedCss, ...rest, children: [
    isCheckmark && /* @__PURE__ */ jsx(Icon, { size: "md", css: {
      flex: "0 0 auto",
      ...adornmentColor ? listRecipe.raw({
        adornmentColor
      }) : {}
    }, children: /* @__PURE__ */ jsx(IconCheckmarkOutline, {}) }),
    children
  ] });
});
ListItem.displayName = "List.Item";
List.Item = ListItem;
export {
  List as default
};
